import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Users, Zap, CheckCircle2, Building2 } from 'lucide-react';

interface QuickStartTemplatesProps {
  hasWorkflows: boolean;
  onTemplateClick: (templateName: string) => void;
}

export const QuickStartTemplates: React.FC<QuickStartTemplatesProps> = ({
  hasWorkflows,
  onTemplateClick,
}) => {
  if (!hasWorkflows) {
    return (
      <Card className="border-dashed">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-primary" />
            <CardTitle className="text-lg">Quick Start Templates</CardTitle>
          </div>
          <p className="text-sm text-muted-foreground">
            Get started quickly with pre-configured workflows
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {/* Template 1: Single Manager */}
            <button
              onClick={() => onTemplateClick('Single Manager')}
              className="flex items-start gap-3 p-3 rounded-lg border-2 border-dashed hover:border-primary hover:bg-accent transition-all text-left group"
            >
              <div className="flex-shrink-0 h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                <Users className="h-5 w-5 text-blue-600" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="font-semibold text-sm">Single Manager</h4>
                  <Badge variant="secondary" className="text-xs">1 Stage</Badge>
                </div>
                <p className="text-xs text-muted-foreground line-clamp-2">
                  Simple one-stage approval by direct manager
                </p>
              </div>
            </button>

            {/* Template 2: Two-Stage */}
            <button
              onClick={() => onTemplateClick('Two-Stage')}
              className="flex items-start gap-3 p-3 rounded-lg border-2 border-dashed hover:border-primary hover:bg-accent transition-all text-left group"
            >
              <div className="flex-shrink-0 h-10 w-10 rounded-lg bg-purple-100 flex items-center justify-center group-hover:bg-purple-200 transition-colors">
                <Building2 className="h-5 w-5 text-purple-600" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="font-semibold text-sm">Two-Stage</h4>
                  <Badge variant="secondary" className="text-xs">2 Stages</Badge>
                </div>
                <p className="text-xs text-muted-foreground line-clamp-2">
                  Manager review followed by department head approval
                </p>
              </div>
            </button>

            {/* Template 3: Auto-Approve */}
            <button
              onClick={() => onTemplateClick('Auto-Approve')}
              className="flex items-start gap-3 p-3 rounded-lg border-2 border-dashed hover:border-primary hover:bg-accent transition-all text-left group"
            >
              <div className="flex-shrink-0 h-10 w-10 rounded-lg bg-green-100 flex items-center justify-center group-hover:bg-green-200 transition-colors">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="font-semibold text-sm">Auto-Approve</h4>
                  <Badge variant="secondary" className="text-xs">No Review</Badge>
                </div>
                <p className="text-xs text-muted-foreground line-clamp-2">
                  No approval required - timesheets auto-approve
                </p>
              </div>
            </button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="flex items-center gap-3 p-3 rounded-lg border border-dashed bg-accent/50">
      <Zap className="h-4 w-4 text-primary flex-shrink-0" />
      <span className="text-sm font-medium">Quick Start:</span>
      <div className="flex items-center gap-2 flex-wrap">
        <Button
          variant="link"
          size="sm"
          className="h-auto p-0 text-sm"
          onClick={() => onTemplateClick('Single Manager')}
        >
          Single Manager
        </Button>
        <span className="text-muted-foreground">•</span>
        <Button
          variant="link"
          size="sm"
          className="h-auto p-0 text-sm"
          onClick={() => onTemplateClick('Two-Stage')}
        >
          Two-Stage
        </Button>
        <span className="text-muted-foreground">•</span>
        <Button
          variant="link"
          size="sm"
          className="h-auto p-0 text-sm"
          onClick={() => onTemplateClick('Auto-Approve')}
        >
          Auto-Approve
        </Button>
      </div>
    </div>
  );
};
